/**
 * Per-attempt FIFO queue for attempt-trail events that must survive a
 * transient network drop. Backed by localStorage under
 * `assessment_event_queue::<attemptId>`. Only events in
 * HIGH_PRIORITY_EVENTS are queued on send failure; everything else is
 * fire-and-forget.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY_PREFIX = 'assessment_event_queue::'
// AI/maintainer note: bump STORAGE_VERSION whenever you make a
// breaking change to the entry shape — add/remove a required field,
// rename a field, or restructure the wrapper. `readQueue` discards
// entries whose `v` doesn't match, so old-shape data is silently
// dropped instead of crashing the new code. Optional fields with
// safe defaults do NOT require a bump.
const STORAGE_VERSION = 1

const MAX_QUEUE_ENTRIES = 200
const MAX_PAYLOAD_BYTES_PER_ENTRY = 4 * 1024
const BATCH_SIZE = 50

// Sender hangs past this → treat as failed. Duplicates handled by F12.
const SENDER_TIMEOUT_MS = 15 * 1000

// Queued on send failure.
const HIGH_PRIORITY_EVENTS = new Set([
    'attempt_exited',
    'banner',
    'cut',
    'copy',
    'paste',
    'dragStart',
    'drop',
    'windowfocus',
    'windowblur',
    'windowhidden',
    'windowvisible',
    'event_queue_dropped',
])

// Persisted to localStorage BEFORE send; removed only on success.
const WRITE_AHEAD_EVENTS = new Set(['attempt_exited', 'banner'])

// ---------------------------------------------------------------------------
// Module setup — localStorage availability probe
// ---------------------------------------------------------------------------

// Probe once. Safari private mode throws on setItem; some embedded
// webviews / strict sandbox policies also throw on access. If probing
// fails the module degrades to no-op; we surface the cause to console
// here, and `isAvailable` lets callers also report it via the trail
// once an attempt exists.
let _probeError = null
const safeStorage = (() => {
    try {
        const probe = '__event_queue_probe__'
        window.localStorage.setItem(probe, '1')
        window.localStorage.removeItem(probe)
        return window.localStorage
    } catch (e) {
        _probeError = e
        console.warn(
            '[eventLogQueue] localStorage unavailable; queue disabled.',
            e?.name || e
        )
        return null
    }
})()

const isAvailable = () => safeStorage !== null
const getUnavailableReason = () =>
    _probeError ? _probeError.name || String(_probeError) : null

// ---------------------------------------------------------------------------
// Boot lifecycle
// ---------------------------------------------------------------------------

/**
 * Sweep stale queues on app boot. Default 24h — assessments cap at 4h
 * and the server auto-submits any abandoned attempt past its end time,
 * so queues older than a day are guaranteed dead weight.
 */
const garbageCollect = (maxAgeMs = 24 * 60 * 60 * 1000) => {
    if (!safeStorage) return
    try {
        const now = Date.now()
        const toRemove = []
        for (let i = 0; i < safeStorage.length; i++) {
            const k = safeStorage.key(i)
            if (!k?.startsWith(STORAGE_KEY_PREFIX)) continue
            try {
                const parsed = JSON.parse(safeStorage.getItem(k))
                const entries = parsed?.entries || []
                const newest = entries.length
                    ? entries[entries.length - 1].queuedAt
                    : 0
                if (now - newest > maxAgeMs) toRemove.push(k)
            } catch (_) {
                toRemove.push(k)
            }
        }
        toRemove.forEach(k => safeStorage.removeItem(k))
    } catch (_) {}
}

// ---------------------------------------------------------------------------
// Main entry — used for every trail event the attempt page emits
// ---------------------------------------------------------------------------

/**
 * Three tiers:
 *  - WRITE_AHEAD_EVENTS: queue first, send, remove on success.
 *  - HIGH_PRIORITY_EVENTS (rest): send first, queue on failure.
 *  - Everything else: send first, drop on failure.
 */
const sendOrEnqueue = async ({ attemptId, isTryout, token, event, sender }) => {
    if (!attemptId || !event?.event) return { ok: false, queued: false }

    stampClientEventId(event)

    const writeAhead = isWriteAhead(event)
    if (writeAhead) enqueue(attemptId, event)

    try {
        await sendWithTimeout(
            sender,
            { attemptId, isTryout, token, events: [event] },
            SENDER_TIMEOUT_MS
        )
        if (writeAhead) removeFromQueue(attemptId, event)
        return { ok: true, queued: false }
    } catch (_) {
        if (writeAhead) return { ok: false, queued: true }
        if (!isQueueable(event)) return { ok: false, queued: false }

        // Oversized payloads: truncate `extras.content`; trailing '...'
        // renders as-is in the trail UI.
        let toQueue = event
        if (JSON.stringify(event).length > MAX_PAYLOAD_BYTES_PER_ENTRY) {
            const content = event?.extras?.content
            if (typeof content === 'string' && content.length > 0) {
                toQueue = {
                    ...event,
                    extras: {
                        ...event.extras,
                        content: content.slice(0, 3 * 1024) + '...',
                    },
                }
            }
        }
        enqueue(attemptId, toQueue)
        return { ok: false, queued: true }
    }
}

// ---------------------------------------------------------------------------
// Synchronous enqueue — called by the unload handler before keepalive
// fires, so the event survives even if the browser dies mid-fetch.
// ---------------------------------------------------------------------------

const enqueue = (attemptId, payload) => {
    stampClientEventId(payload)
    const queue = readQueue(attemptId)
    queue.push({
        event: payload.event,
        payload,
        queuedAt: Date.now(),
    })

    // FIFO overflow: drop oldest user events and the bookkeeping marker
    // takes the freed slot, so total stays at exactly MAX_QUEUE_ENTRIES.
    if (queue.length > MAX_QUEUE_ENTRIES) {
        const dropCount = queue.length - MAX_QUEUE_ENTRIES + 1
        const trimmed = queue.slice(dropCount)
        const droppedPayload = {
            event: 'event_queue_dropped',
            extras: { dropped_count: dropCount, cap: MAX_QUEUE_ENTRIES },
        }
        stampClientEventId(droppedPayload)
        trimmed.push({
            event: 'event_queue_dropped',
            payload: droppedPayload,
            queuedAt: Date.now(),
        })
        writeQueue(attemptId, trimmed)
        return
    }

    writeQueue(attemptId, queue)
}

// ---------------------------------------------------------------------------
// Drain — invoked on mount, on `online`, and on a 30s safety-net poll
// ---------------------------------------------------------------------------

/**
 * Drain the queue in batches. Stops on first failed batch and leaves
 * the rest queued for the next flush.
 */
const flush = async ({ attemptId, isTryout, token, sender }) => {
    if (!attemptId) return { flushed: 0, remaining: 0 }
    const queue = readQueue(attemptId)
    if (!queue.length) return { flushed: 0, remaining: 0 }

    let flushed = 0
    let cursor = 0

    while (cursor < queue.length) {
        const batch = queue.slice(cursor, cursor + BATCH_SIZE)
        const events = batch.map(e => ({
            ...e.payload,
            extras: {
                ...(e.payload.extras || {}),
                // Signals the admin panel that this event was delivered
                // late — typically the student was offline when it
                // happened. `queued_at_ms` is the original timestamp.
                was_queued: true,
                queued_at_ms: e.queuedAt,
            },
        }))

        try {
            await sender({ attemptId, isTryout, token, events })
            flushed += batch.length
            cursor += BATCH_SIZE
        } catch (_) {
            break
        }
    }

    // Re-read the queue before writing back: sendOrEnqueue may have
    // appended new entries while we were awaiting the sender. Remove
    // only the ones we actually flushed (by client_event_id).
    const flushedIds = new Set(
        queue
            .slice(0, cursor)
            .map(e => e.payload?.extras?.client_event_id)
            .filter(Boolean)
    )
    const current = readQueue(attemptId)
    const remaining = current.filter(
        e => !flushedIds.has(e.payload?.extras?.client_event_id)
    )
    writeQueue(attemptId, remaining)
    return { flushed, remaining: remaining.length }
}

// ---------------------------------------------------------------------------
// Status / cleanup
// ---------------------------------------------------------------------------

const queueSize = attemptId => readQueue(attemptId).length

const clear = attemptId => {
    if (!safeStorage || !attemptId) return
    try {
        safeStorage.removeItem(keyFor(attemptId))
    } catch (_) {}
}

const clearOtherAttempts = activeAttemptId => {
    if (!safeStorage || !activeAttemptId) return
    try {
        const activeKey = keyFor(activeAttemptId)
        const toRemove = []
        for (let i = 0; i < safeStorage.length; i++) {
            const key = safeStorage.key(i)
            if (key?.startsWith(STORAGE_KEY_PREFIX) && key !== activeKey) {
                toRemove.push(key)
            }
        }
        toRemove.forEach(key => safeStorage.removeItem(key))
    } catch (_) {}
}

// ---------------------------------------------------------------------------
// Internal helpers — storage primitives + classification
// ---------------------------------------------------------------------------

const keyFor = attemptId => `${STORAGE_KEY_PREFIX}${attemptId}`

const readQueue = attemptId => {
    if (!safeStorage || !attemptId) return []
    try {
        const raw = safeStorage.getItem(keyFor(attemptId))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (parsed?.v !== STORAGE_VERSION) return []
        return Array.isArray(parsed.entries) ? parsed.entries : []
    } catch (_) {
        return []
    }
}

const writeQueue = (attemptId, entries) => {
    if (!safeStorage || !attemptId) return
    try {
        if (!entries.length) {
            safeStorage.removeItem(keyFor(attemptId))
            return
        }
        safeStorage.setItem(
            keyFor(attemptId),
            JSON.stringify({ v: STORAGE_VERSION, entries })
        )
    } catch (_) {
        // Quota exceeded — drop oldest half and retry once.
        if (entries.length > 1) {
            const half = entries.slice(Math.floor(entries.length / 2))
            try {
                safeStorage.setItem(
                    keyFor(attemptId),
                    JSON.stringify({ v: STORAGE_VERSION, entries: half })
                )
            } catch (_) {}
        }
    }
}

const isQueueable = event =>
    HIGH_PRIORITY_EVENTS.has(event?.event) || event?.priority === 'high'

const isWriteAhead = event =>
    WRITE_AHEAD_EVENTS.has(event?.event) ||
    event?.priority === 'write_ahead'

const removeFromQueue = (attemptId, event) => {
    const targetId = event?.extras?.client_event_id
    if (!targetId) return
    const current = readQueue(attemptId)
    const next = current.filter(
        e => e.payload?.extras?.client_event_id !== targetId
    )
    if (next.length !== current.length) writeQueue(attemptId, next)
}

const sendWithTimeout = (sender, args, timeoutMs) => {
    let timer
    return Promise.race([
        sender(args).finally(() => clearTimeout(timer)),
        new Promise((_, reject) => {
            timer = setTimeout(
                () => reject(new Error(`sender timeout after ${timeoutMs}ms`)),
                timeoutMs
            )
        }),
    ])
}

// UUID generator for event payloads. Same id is stamped on the event
// object before it's sent (live, via keepalive, or via queue flush)
// so the backend can dedup duplicate deliveries by
// (attempt_id, client_event_id).
const generateEventId = () => {
    const cryptoObj = typeof crypto !== 'undefined' ? crypto : null

    if (typeof cryptoObj?.randomUUID === 'function') {
        return cryptoObj.randomUUID()
    }

    if (typeof cryptoObj?.getRandomValues !== 'function') return null

    const bytes = new Uint8Array(16)
    cryptoObj.getRandomValues(bytes)
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0'))
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
        .slice(6, 8)
        .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

// Mutates the event in place to ensure it carries a client_event_id.
// Both delivery paths (live send via sender, queue persistence via
// enqueue) call this so callers don't have to think about it.
const stampClientEventId = event => {
    if (!event || typeof event !== 'object') return event
    if (!event.extras) event.extras = {}
    if (!event.extras.client_event_id) {
        const clientEventId = generateEventId()
        if (clientEventId) event.extras.client_event_id = clientEventId
    }
    return event
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    sendOrEnqueue,
    enqueue,
    flush,
    queueSize,
    clear,
    clearOtherAttempts,
    garbageCollect,
    isAvailable,
    getUnavailableReason,
    generateEventId,
}
